import {
  both,
  ifElse,
  path,
  pipe,
  propOr,
  pathOr,
  reduce,
  any,
  values,
  flatten,
} from 'ramda';
import {
  some,
  map,
  sortBy,
  find,
  filter,
  get,
} from 'lodash/fp';
import moment from 'moment';
import {
  getActiveShop,
  getActiveShopOrAnyBrandShopFlag,
  isBarberUser,
  isBrandViewActive,
  getShops,
} from '../../auth/selectors';
import { DEFAULT_TRACKING_ROLE } from '../constants';

export const timeTrackingFlagEnabled = getActiveShopOrAnyBrandShopFlag('timeTrackingEnabled');
export const calculateHourlyWagesEnabled = ifElse(
  isBrandViewActive,
  pipe(getShops, any(propOr(false, 'calculateHourlyWages'))),
  pipe(getActiveShop, propOr(false, 'calculateHourlyWages')),
);

export const extendedHourlyWagesEnabled = ifElse(
  isBrandViewActive,
  pipe(getShops, any(pathOr(false, ['settings', 'extendedHourlyWages']))),
  pipe(getActiveShop, pathOr(false, ['settings', 'extendedHourlyWages'])),
);

export const requirePasscodeEnabled = ifElse(
  isBrandViewActive,
  pipe(getShops, any(propOr(false, 'permissionPasscodeEnabled'))),
  pipe(getActiveShop, propOr(false, 'permissionPasscodeEnabled')),
);

export const adpReportEnabled = ifElse(
  isBrandViewActive,
  pipe(getShops, any(propOr(false, 'adpReport'))),
  pipe(getActiveShop, propOr(false, 'adpReport')),
);

export const adpExportEnabled = both(
  calculateHourlyWagesEnabled,
  adpReportEnabled,
);

const timeTrackPermission = path(['auth', 'permissions', 'timeTrack', 'view']);
export const timeTrackingEnabled = ifElse(
  isBarberUser,
  timeTrackingFlagEnabled,
  both(
    timeTrackingFlagEnabled,
    timeTrackPermission,
  ),
);

const shouldTrackEmployeeTime = state => state.auth.trackEmployeeTime;

export const shouldShowClockInButton = both(
  timeTrackingEnabled,
  shouldTrackEmployeeTime,
);

export const wasTimerFetched = state => (state.auth.trackEmployeeTime ? !!state.timer.fetchedTimer : true);

export const shouldOpenClockInModal = state =>
  !!state.auth.trackEmployeeTime
  && !!state.timer.fetchedTimer
  && !state.timer.activeTimer
  && !state.timer.modalSuppresed;
export const shouldOpenClockOutModal =
  state => state.auth.trackEmployeeTime && state.timer.fetchedTimer && state.timer.activeTimer;
export const isTimerActive = state => state.timer.fetchedTimer && state.timer.activeTimer;

const getBarberEntries = (state, id) => path(['timer', 'barbers', id], state);


export const getBarberActive = pipe(
  getBarberEntries,
  some({ active: true }),
);

export const getBarberActiveRole = pipe(
  getBarberEntries,
  find({ active: true }),
  path(['role'])
);

export const getActiveBarberIds = pipe(
  path(['timer', 'barbers']),
  values,
  flatten,
  filter({ active: true }),
  map(get('barberId')),
);

const getEntryDuration = (entry) => {
  const startedAt = moment(entry.startedAt);
  const endedAt = moment(entry.endedAt || new Date());
  const dur = moment.duration(moment(endedAt).diff(startedAt));

  return moment.duration(dur.asMinutes(), 'm');
};
export const formatDuration = duration => `${duration.hours()}h ${duration.minutes()}m`;

export const getFormattedDuration = pipe(getEntryDuration, formatDuration);

export const getBarberClockedTime = (state, id) => {
  const barberEntries = getBarberEntries(state, id);

  if (!barberEntries) {
    return '0h 0m';
  }

  const totalClocked = reduce(
    (acc, entry) => acc.add(getEntryDuration(entry)),
    moment.duration(),
    barberEntries,
  );

  const activeEntry = find({ active: true }, barberEntries);

  if (!activeEntry) {
    return formatDuration(totalClocked);
  }

  const sameAsActiveEntries = filter(
    entry => entry.role === activeEntry.role,
    barberEntries
  );

  const lastActiveRoleClocked = reduce(
    (acc, entry) => acc.add(getEntryDuration(entry)),
    moment.duration(),
    sameAsActiveEntries,
  );

  return formatDuration(lastActiveRoleClocked);
};

export const getFormattedBarberEntries = pipe(
  getBarberEntries,
  map(entry => ({
    id: entry.id,
    barberId: entry.barberId,
    startedAt: entry.startedAt,
    endedAt: entry.endedAt,
    userId: entry.userId,
    active: entry.active,
    role: entry.role || DEFAULT_TRACKING_ROLE,
  })),
  sortBy('startedAt'),
);

export const forceClockInBeforeSaleEnabled = pipe(
  getActiveShop,
  pathOr(false, ['settings', 'forceClockInBeforeSale'])
);

